import { connect } from 'react-redux';
import React, { Component } from 'react';

import actions from '../../actions';
import Button from '../Button/Button';
import FontAwesome from 'react-fontawesome';
import { withRouter } from 'react-router-dom';

import Footer from './Footer';
import Header from './Header';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import Spinner from '../Spinner/Spinner';
import { AlphanumericInput } from '@lmig/lmds-react-alphanumeric-input';
import { Form } from '@lmig/lmds-react-form';
import TextBox from '../TextBox/TextBox';
import { HotKeys } from 'react-hotkeys';
import Failure from './Failure';
import { unlockDuration } from '../../modules/time';

const keyMap = {
    close: 'escape',
    submit: 'enter'
};

export class SecurityQuestion extends Component {
    constructor() {
        super();
        this.userAnswer = React.createRef();

        this.state = {
            userAnswer: '',
            isLocked: false,
            questions: [],
            questionIndex: 0,
            submittedAnswersCounter: 0
        };
    }

    componentDidMount() {
        this.props.requestQuestions(this.props.customer, this.props.app.deploy.env);
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.customer.questions.length !== prevState.questions.length) {
            return { questions: nextProps.customer.questions };
        }

        if (nextProps.customer.submittedAnswersCounter !== prevState.submittedAnswersCounter) {
            return { submittedAnswersCounter: nextProps.customer.submittedAnswersCounter };
        }

        const hasWrongAnswers = nextProps.customer.questions.some(
            q => q.isAnsweredCorrectly !== undefined && !q.isAnsweredCorrectly
        );

        const isCurrentIndexWrongAnswer =
            prevState.questions.length > 0 &&
            prevState.questions[prevState.questionIndex].isAnsweredCorrectly === false;

        if (
            nextProps.customer.questions.length >= 1 &&
            hasWrongAnswers &&
            isCurrentIndexWrongAnswer
        ) {
            let index = prevState.questionIndex;
            if (
                prevState.questionIndex + 1 > prevState.questions.length - 1 ||
                nextProps.customer.isLocked
            ) {
                return { isLocked: true };
            } else {
                index = index + 1;
            }
            return { questionIndex: index };
        }
        return null;
    }

    onSubmit() {
        const {
            accessCode,
            apigee: { jwtToken }
        } = this.props.customer;

        this.props.validateAnswer(
            {
                userAnswer: this.state.userAnswer,
                questionID: this.state.questions[this.state.questionIndex].id,
                accessCode
            },
            {
                jwtToken,
                env: this.props.app.deploy.env
            }
        );
        this.setState({ userAnswer: '' });
    }

    onAnswerChange(e) {
        this.setState({ userAnswer: e.target.rawValue || e.target.value });
    }

    keyHandlers() {
        return {
            submit: () => (!this.isButtonDisabled() ? this.onSubmit() : null),
            close: () => this.props.onCancelClick()
        };
    }

    isButtonDisabled() {
        if (this.state.questions.length === 0) return null;

        const questionIndex = this.state.questionIndex;
        const isFreeTextQuestion = this.state.questions[questionIndex].type === 'text';
        const isMultipleChoiceQuestion = this.state.questions[questionIndex].type === 'radio';
        const isPhoneNumberQuestion =
            this.state.questions[questionIndex].placeholder === 'Phone number';
        const phoneNumberLength = 10;
        const drivingLicenseLength = 4;

        if (isFreeTextQuestion) {
            if (isPhoneNumberQuestion) return this.state.userAnswer.length < phoneNumberLength;
            return this.state.userAnswer.length < drivingLicenseLength;
        } else if (isMultipleChoiceQuestion) {
            return !this.state.userAnswer;
        }
    }

    renderQuestions() {
        const questionIndex = this.state.questionIndex;
        if (this.state.questions.length === 0) return;
        const questions = this.state.questions;

        if (questions.length > 0) {
            if (questions[questionIndex].type === 'radio') {
                return (
                    <MultipleChoiceQuestion
                        onAnswerChange={e => this.onAnswerChange(e)}
                        {...questions[questionIndex]}
                        {...this.props}
                        {...this.state}
                    />
                );
            }

            if (questions[questionIndex].type === 'text') {
                return (
                    <div>
                        <HotKeys keyMap={keyMap} handlers={this.keyHandlers()}>
                            <p className="question">{questions[questionIndex].question}</p>
                            {questions[questionIndex].placeholder === 'Phone number' ? (
                                <Form id="phone-number">
                                    <AlphanumericInput
                                        datacy="phone-number"
                                        id="phone-number"
                                        onChange={e => this.onAnswerChange(e)}
                                        labelVisual={questions[questionIndex].placeholder}
                                        mask="phone"
                                        name={questions[questionIndex].placeholder}
                                        value={this.state.userAnswer}
                                    />
                                </Form>
                            ) : (
                                <TextBox
                                    marginLeft={0}
                                    inputRef={this.userAnswer}
                                    placeholder={questions[questionIndex].placeholder}
                                    name={questions[questionIndex].placeholder}
                                    onChange={e => this.onAnswerChange(e)}
                                />
                            )}
                        </HotKeys>
                    </div>
                );
            }
        }
    }

    render() {
        const { isLocked, lastFailedAttemptTimeStamp } = this.props.customer;
        const minLeft = unlockDuration(lastFailedAttemptTimeStamp, 15);

        if (isLocked && minLeft > 0) {
            return (
                <HotKeys keyMap={keyMap} handlers={this.keyHandlers()}>
                    <Header canBeClosed onCancelClick={this.props.onCancelClick} />
                    <Failure
                        text={`We weren't able to find a match to the information you provided. You can try again in ${minLeft} minutes or contact your agent for information on your policy.`}
                    />
                </HotKeys>
            );
        }

        return (
            <React.Fragment>
                <Header {...this.props}>
                    <h3 datacy="security-header">
                        <FontAwesome name="shield" size="2x" className="primary-yellow" />
                        Security Questions
                    </h3>
                </Header>
                <div className="modal-body">
                    <div>
                        {!this.props.customer.isLoading ? (
                            'While we pull together your policy, answer a few questions to verify your identity. We’ll check your answers against the information your agent shared with us.'
                        ) : (
                            <Spinner />
                        )}
                    </div>
                    {this.renderQuestions()}
                </div>

                {!this.props.customer.isLoading ? (
                    <Footer {...this.props}>
                        <Button
                            disabled={this.isButtonDisabled()}
                            type="primary"
                            text="OK"
                            onClick={() => this.onSubmit()}
                            datacy="OK-button"
                        />
                    </Footer>
                ) : null}
            </React.Fragment>
        );
    }
}

export function mapStoreToProps(store) {
    return {
        customer: store.customer,
        app: store.app
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        validateAnswer: (data, tokens) =>
            dispatch(actions.customer.requestSecurityQuestionValidation({ tokens, data })),
        requestQuestions: (data, env) =>
            dispatch(actions.customer.requestSecurityQuestions({ env, ...data }))
    };
}

export default withRouter(
    connect(
        mapStoreToProps,
        mapDispatchToProps
    )(SecurityQuestion)
);



// WEBPACK FOOTER //
// ./src/components/Modal/SecurityQuestion.jsx